// Legacy FinancialRecord for backward compatibility
export interface LegacyFinancialRecord {
  id: string;
  dealerCode: string;
  dealerName: string;
  year: number;
  month: number;
  brand: string;
  locationId?: number;
  groupId?: number | null;
  groupName?: string | null;
  newVehicleUnitsSold: number;
  newVehicleSalesRevenue: number;
  newVehicleGrossProfit: number;
  usedVehicleUnitsSold?: number;
  usedVehicleSalesRevenue?: number;
  usedVehicleGrossProfit?: number;
  financeInsuranceIncome?: number;
  partsServiceRevenue?: number;
  partsServiceGrossProfit?: number;
  totalDirectExpenses?: number;
  personnelExpenses?: number;
  advertisingExpenses?: number;
  totalDirectProfit?: number;
  
  // New financial data points
  newVehiclesDiscountAmount?: number;
  newVehiclesFinancePenetration?: number;
  serviceLabourSalesCostofSales?: number;
  serviceLabourSalesHoursSold?: number;
  serviceLabourSalesNetSales?: number;
  usedVehiclesOtherMakesUnitsSold?: number;
  
  // Additional financial data points
  ytdTotalRevenue?: number;
  ytdTotalRevenueNewVehicles?: number;
  ytdProfit?: number;
  ytdProfitPercentage?: number;
  newVehicleFleetUnits?: number;
  newVehicleFleetSales?: number;
  dealershipBonusAmount?: number;
  newVehicleDemoStat?: number;
  newVehicleTradeInSameBrand?: number;
  newVehicleTradeInOtherMakes?: number;
  usedVehicleTradeInSameBrand?: number;
  usedVehicleTradeInOtherMakes?: number;
  newVehicleDepartmentalExpenses?: number;
  usedVehicleDepartmentalExpenses?: number;
  labourChargeoutRate?: number;
  
  createdAt: string;
  updatedAt: string;
}

// New KPI-based structure
export interface KPI {
  code: string;        // e.g., "A0100.1"
  name: string;        // e.g., "New Vehicle Units Sold"
  value: number;
  category?: string;   // e.g., "Sales", "Finance", "Service"
  subcategory?: string;
  unit?: string;       // e.g., "units", "currency", "percentage"
  description?: string;
}

export interface FinancialRecord {
  id: string;
  dealer: string;
  brand: string;
  year: number;
  month: number; // 1-12 for months
  location_id?: number; // Location ID for hierarchy-based permissions
  kpis: KPI[];
  groups?: string[]; // Array of group names the dealer is assigned to
  userId?: string; // ID of the user who created this record
  createdAt: string;
  updatedAt: string;
  additionalData?: { field: string; value: string }[]; // Additional columns from CSV
}

// Input format for financial records
export interface FinancialRecordInput {
  dealer: string;
  brand: string;
  year: number;
  month: number;
  kpis: {
    code: string;
    name: string;
    value: number;
    category?: string;
    subcategory?: string;
    unit?: string;
  }[];
  additionalData?: { field: string; value: string }[]; // Additional columns from CSV
}

// KPI enrichment for adding metadata to detected KPIs
export interface KPIEnrichment {
  code: string;
  displayName: string;
  description: string;
  category: string;
  subcategory?: string;
  unit: string;
  dataType: 'currency' | 'units' | 'percentage' | 'rate' | 'text';
}

// Predefined KPI categories
export enum KPICategory {
  SALES = 'Sales',
  FINANCE = 'Finance',
  FINANCIAL = 'Financial', // Added Financial as equivalent to Finance
  SERVICE = 'Service',
  PARTS = 'Parts',
  MARKETING = 'Marketing',
  OPERATIONS = 'Operations',
  PERFORMANCE = 'Performance',
  OTHER = 'Other'
}

// Filter interface for querying financial records
export interface FinancialRecordFilter {
  brands?: string[];
  dealers?: string[];
  years?: number[];
  months?: number[];
  kpiCodes?: string[];
  kpiCategories?: string[];
  owners?: string[]; // User IDs to filter by owner
  page?: number;
  limit?: number;
  sortBy?: 'dealer' | 'brand' | 'year' | 'month' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

// Summary interface for aggregated data
export interface FinancialSummary {
  totalRecords: number;
  uniqueBrands: number;
  uniqueDealers: number;
  totalKPIs: number;
  monthRange: {
    startYear: number;
    endYear: number;
    startMonth: number;
    endMonth: number;
  };
  kpiBreakdown: {
    category: string;
    count: number;
  }[];
}

// Response interface for paginated financial records
export interface FinancialRecordsResponse {
  records: FinancialRecord[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary?: FinancialSummary;
}

// For backward compatibility - export the old interface as well
export type { LegacyFinancialRecord as FinancialRecord_Legacy }; 